import { cn } from "@u/cn";
import { useIsMobile } from "@h/useResponsive";
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover";
import { Dialog, DialogContent, DialogTitle } from "@ui/dialog";
import ResponsiveSheet from "@ui/responsive-sheet";
import { Clock } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Canonical time-of-day picker. A styled trigger that opens a two-column
 * (hours + minutes) picker — consistent cross-browser and in dark mode, unlike
 * the native `type="time"`. Desktop → popover; mobile → bottom sheet with
 * touch-sized rows. Emits/consumes a `"HH:mm"` string.
 */
const pad = (n: number) => String(n).padStart(2, "0");

function Column({
  values,
  selected,
  onSelect,
  label,
  touch,
}: {
  values: string[];
  selected: string;
  onSelect: (v: string) => void;
  label: string;
  touch?: boolean;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  // Center the selected row when the picker opens.
  useEffect(() => {
    ref.current?.scrollIntoView({ block: "center" });
  }, []);
  return (
    <div className="flex flex-1 flex-col min-w-0">
      <span className="label-nano text-center text-muted-foreground py-1.5 border-b border-border/50">
        {label}
      </span>
      <div className="flex-1 overflow-y-auto custom-scrollbar px-1 py-1">
        {values.map((v) => {
          const active = v === selected;
          return (
            <button
              key={v}
              ref={active ? ref : undefined}
              type="button"
              onClick={() => onSelect(v)}
              className={cn(
                "w-full rounded-lg text-center font-mono tabular-nums transition-colors",
                touch ? "py-3 text-base" : "px-4 py-1.5 text-sm",
                active
                  ? "bg-primary-strong text-primary-foreground"
                  : "text-foreground hover:bg-muted/60",
              )}
            >
              {v}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function TimeInput({
  value,
  onChange,
  className,
  minuteStep = 1,
  desktopDialog = false,
  "aria-label": ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  className?: string;
  /** Minute granularity in the picker (default 1). */
  minuteStep?: number;
  /** Desktop → centered dialog instead of an anchored popover (for cramped/nested triggers). */
  desktopDialog?: boolean;
  "aria-label"?: string;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const hh = value ? value.slice(0, 2) : "";
  const mm = value ? value.slice(3, 5) : "";
  const hours = Array.from({ length: 24 }, (_, i) => pad(i));
  const minutes = Array.from(
    { length: Math.ceil(60 / minuteStep) },
    (_, i) => pad(i * minuteStep),
  );

  const columns = (touch: boolean) => (
    <div
      className={cn(
        "flex divide-x divide-border/50",
        touch ? "h-64" : "h-52",
      )}
    >
      <Column
        label="h"
        values={hours}
        selected={hh}
        touch={touch}
        onSelect={(h) => onChange(`${h}:${mm || "00"}`)}
      />
      <Column
        label="min"
        values={minutes}
        selected={mm}
        touch={touch}
        onSelect={(m) => onChange(`${hh || "00"}:${m}`)}
      />
    </div>
  );

  const trigger = (onClick?: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        "inline-flex h-9 items-center gap-2 rounded-xl border border-border bg-background px-3 text-sm font-mono tabular-nums",
        "transition-colors hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-primary/30",
        !value && "text-muted-foreground",
        className,
      )}
    >
      <Clock size={14} className="text-muted-foreground shrink-0" />
      {value || "--:--"}
    </button>
  );

  if (isMobile) {
    return (
      <>
        {trigger(() => setOpen(true))}
        <ResponsiveSheet
          open={open}
          onOpenChange={setOpen}
          title={ariaLabel ?? "—"}
        >
          {open && <div className="px-2 pb-2">{columns(true)}</div>}
        </ResponsiveSheet>
      </>
    );
  }

  if (desktopDialog) {
    return (
      <>
        {trigger(() => setOpen(true))}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent size="sm" className="p-4">
            <DialogTitle className="sr-only">{ariaLabel ?? "—"}</DialogTitle>
            {open && columns(false)}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger()}</PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        {open && columns(false)}
      </PopoverContent>
    </Popover>
  );
}
